// @workflow assistant.ui-tools
/**
 * The tag UI tools: `ui.tags.open_panel` opens a tag's card (on one of its tabs when asked: Documents, Tasks, an AVA Inspection's Findings…), `ui.tags.open_360` opens the tag in
 * 360 and `ui.tags.show_in_2d` shows it on its level's 2D plan, each the way the facility deep link
 * (`?tagId=…&openPanel=true` / `&zoom360=true` / `&show2d=true`) does it, but run in place, so they
 * work inside the facility the user already has open (Khalid, 2026-10-14).
 * Isolating a tag in 3D is `ui.viewer.focus_tags` with mode 'isolate' (viewerTools.js).
 * Pure decisions only; `services/viewerHost.js` wires them to the app.
 *
 * All three need the facility's page, loaded, not a 3D model: they prepare with PREPARE_NEED.FACILITY
 * (viewerPrepare.js), which opens another facility first when `facilityId` names one.
 *
 * Host contract (`host.viewer`), beyond the prepare calls listed in viewerPrepare.js:
 *   openTagPanel(tagId, tab) → Promise<{ status }>   TAGS/NAVIGATE_TAG_SPATIALLY, intent 'info': the card in the
 *                       panel for the tag's kind (Tags, Inspection or AVA Inspection), on `tab` (a card tab's name, Tags/utils/tagInfoTabs.js);
 *                       status 'no-such-tab' (+ `tabs`, the ones it has) when its card has no `tab`
 *   openTagIn360(tagId, signal) → Promise<{ status }|null>   MainView.openTagIn360, the ?zoom360
 *                       pipeline; null when the facility page can't run it; `signal` withdraws it
 *   showTagIn2d(tagId, signal) → Promise<{ status, levelName?, planUrn?, highlighted: () => boolean|null }>
 *                       the shared action (actions/tagIn2d.js); `highlighted` is null until the plan is
 *                       there, then whether the tag is lit on it; `signal` stops the plan change and the highlight
 *   readPlan2d() → { urn, loaded, paneVisible }   the plan on the page, and whether a 2D view shows
 *
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable, failed } from './uiToolContract.js';
import { VIEWER_DETAIL, VIEWER_WAIT } from './viewerContract.js';
import { checkTagArgs } from './viewerArgs.js';
import { PREPARE_NEED, prepareViewer, deadlineOf, SYSTEM_CLOCK } from './viewerPrepare.js';
import { TAG_2D_STATUS } from '../../../actions/tagIn2d.js';
import { CAPABILITY } from '../../../actions/permissions.js';
import { ALL_TAG_TABS, TAG_TAB, TAG_TAB_LABEL } from '../../Tags/utils/tagInfoTabs.js';

/** What the two app paths answer (tagSpatialNavigationExecutor.js, popup360Navigation.js), and the host's own answer. */
export const TAG_STATUS = Object.freeze({
  PANEL_OPENED: 'panel-opened',
  EXECUTED: 'executed',
  STALE: 'stale',
  UNAVAILABLE: 'unavailable',
  /** The saved scan is gone and the user declined the nearest one (popup360FallbackConfirmation). */
  CANCELLED: 'cancelled',
  /** The host's own answer: the tag's card has no such tab (`tabs` = the ones it has, Tags/utils/tagInfoTabs.js). */
  NO_SUCH_TAB: 'no-such-tab',
});

/** Plain words for the model, and for the user when a button shows them. */
export const TAG_DETAIL = Object.freeze({
  PANEL_FAILED: 'AVA could not open this tag. It may not be in this facility.',
  NO_360: 'This tag has no 360 image in this facility.',
  DECLINED: 'The user chose not to open the nearest 360 image.',
  OPEN_360_FAILED: 'AVA could not open this tag in 360.',
  NO_PAGE: 'The facility page is not ready to open this tag. Try again in a moment.',
  NOT_IN_FACILITY: "AVA couldn't find this tag in this facility.",
  NO_PLACE_2D: "This tag has no position, so it can't be shown on a 2D plan.",
  NO_PLAN: "This tag isn't on any 2D plan of this facility.",
  PLAN_NOT_MAPPED: "This facility's 2D plan isn't lined up with the 3D model yet, so AVA can't place the tag on it.",
  PLAN_NOT_SHOWN: "The 2D plan didn't open with the tag on it. Try again in a moment.",
});

const statusOf = (outcome) => (outcome && outcome.status) || null;

/**
 * open_panel: the tag tools' `{ tagId, facilityId? }` plus `tab?`, a card tab's name (Tags/utils/tagInfoTabs.js
 * TAG_TAB; absent or null = info). Whether this tag's card has it is the host's to read. Null for `unsupported`,
 * an unknown tab name included.
 */
export function checkOpenPanelArgs(args) {
  const tag = checkTagArgs(args);
  if (!tag) return null;
  const tab = args.tab === undefined || args.tab === null ? TAG_TAB.INFO : args.tab;
  return ALL_TAG_TABS.includes(tab) ? { ...tag, tab } : null;
}

/** "This item's card has no Monitoring tab. It has Info, Documents, Tasks and Records." */
export function noSuchTabDetail(tab, tabs) {
  const names = (Array.isArray(tabs) && tabs.length ? tabs : [TAG_TAB.INFO]).map((name) => TAG_TAB_LABEL[name] || name);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `This item's card has no ${TAG_TAB_LABEL[tab] || tab} tab. It has ${list}.`;
}

function panelResult(outcome, tab) {
  const status = statusOf(outcome);
  if (status === TAG_STATUS.PANEL_OPENED) return ok();
  if (status === TAG_STATUS.NO_SUCH_TAB) return notAvailable(noSuchTabDetail(tab, outcome.tabs));
  return failed(status === TAG_STATUS.STALE ? VIEWER_DETAIL.REPLACED : TAG_DETAIL.PANEL_FAILED);
}

const PANORAMA_ANSWER = Object.freeze({
  [TAG_STATUS.EXECUTED]: () => ok(),
  [TAG_STATUS.STALE]: () => failed(VIEWER_DETAIL.REPLACED),
  [TAG_STATUS.UNAVAILABLE]: () => notAvailable(TAG_DETAIL.NO_360),
  [TAG_STATUS.CANCELLED]: () => failed(TAG_DETAIL.DECLINED),
});

function panoramaResult(outcome) {
  if (!outcome) return notAvailable(TAG_DETAIL.NO_PAGE);
  const status = statusOf(outcome);
  return Object.prototype.hasOwnProperty.call(PANORAMA_ANSWER, status) ? PANORAMA_ANSWER[status]() : failed(TAG_DETAIL.OPEN_360_FAILED);
}

const prepareFacility = (host, input) => prepareViewer(host, input.facilityId, PREPARE_NEED.FACILITY);

/** Why the shared action could not start, in plain words. Anything else unknown is never success. */
const PLAN_REFUSAL = Object.freeze({
  [TAG_2D_STATUS.NOT_FOUND]: TAG_DETAIL.NOT_IN_FACILITY,
  [TAG_2D_STATUS.NO_PLACE]: TAG_DETAIL.NO_PLACE_2D,
  [TAG_2D_STATUS.NO_LEVEL]: TAG_DETAIL.NO_PLAN,
  [TAG_2D_STATUS.NOT_MAPPED]: TAG_DETAIL.PLAN_NOT_MAPPED,
});

/** On screen: a 2D view shows, its plan is the level's and has loaded, and the tag is lit on it. */
function planShowsTag(plan, started) {
  return !!plan && plan.paneVisible && plan.loaded && plan.urn === started.planUrn && started.highlighted() === true;
}

/**
 * Show the tag on its plan, then read the page back before answering: "shown" only once the plan
 * is on screen with the tag highlighted (bounded like the viewer waits, and stopped with the request).
 */
async function showOnPlan({ tagId }, host) {
  const signal = host.signal || null;
  const started = await host.viewer.showTagIn2d(tagId, signal);
  if (signal && signal.aborted) return failed(VIEWER_DETAIL.STOPPED);
  const status = started && started.status;
  if (status !== TAG_2D_STATUS.STARTED) {
    const refusal = Object.prototype.hasOwnProperty.call(PLAN_REFUSAL, status) ? PLAN_REFUSAL[status] : TAG_DETAIL.PLAN_NOT_SHOWN;
    return notAvailable(refusal);
  }
  const clock = host.clock || SYSTEM_CLOCK;
  const deadline = deadlineOf(clock.now(), host.expiresAt);
  for (;;) {
    if (signal && signal.aborted) return failed(VIEWER_DETAIL.STOPPED);
    // The plan came, but its labels could not light the tag: say so now, not at the time limit.
    if (started.highlighted() === false) return notAvailable(TAG_DETAIL.PLAN_NOT_SHOWN);
    if (planShowsTag(host.viewer.readPlan2d(), started)) return ok({ shown: true, level: started.levelName || null });
    if (clock.now() >= deadline) return notAvailable(TAG_DETAIL.PLAN_NOT_SHOWN);
    await clock.sleep(VIEWER_WAIT.POLL_MS, signal);
  }
}

export const TAG_ACTIONS = Object.freeze({
  [UI_TOOL.TAGS_OPEN_PANEL]: {
    check: checkOpenPanelArgs,
    ready: prepareFacility,
    run: async ({ tagId, tab }, host) => panelResult(await host.viewer.openTagPanel(tagId, tab), tab),
  },
  [UI_TOOL.TAGS_OPEN_360]: {
    check: checkTagArgs,
    ready: prepareFacility,
    run: async ({ tagId }, host) => panoramaResult(await host.viewer.openTagIn360(tagId, host.signal || null)),
  },
  [UI_TOOL.TAGS_SHOW_IN_2D]: {
    // The tag card's 2D button's permission: the runner refuses before anything is prepared.
    capability: CAPABILITY.TAGS_SHOW_IN_2D,
    check: checkTagArgs,
    ready: prepareFacility,
    run: showOnPlan,
  },
});
